import { SelectFilter } from '@/infra/SelectFilter'
import type {
  ListWorkspaceMembershipsFn,
  GetCurrentUserWorkspaceMembershipsFn,
} from '@/features/iam'
import {
  type FilterItem,
  FilterItemSource,
  type FilterStorage,
  FiltersViewModel,
} from '@/infra/FiltersViewModel'
import { observable, computed, action } from 'mobx'
import { toStream, tapAction } from '@/utils/mobx-util'
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  map,
  switchMap,
  tap,
} from 'rxjs'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { type Id } from '@/infra/id'
import { type TableSortOrder } from '@/components/Table'
import { deepEqual } from '@/utils/object-util'
import { shareLatest, withAbortSignal } from '@/utils/rx-util'
import {
  type BookkeepingPendingAction,
  type BookkeepingManagerAction,
} from './BookkeepingPendingAction'
import { SingleSelectFilter } from '@/infra/SingleSelectFilter'
import {
  type BookkeepingPendingActionSortField,
  type BookkeepingManagerStore,
} from '@/features/admin/bookkeeping/BookkeepingManagerStore'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import type { AlertRetryWrapperFactory } from '@/features/shell'

/**
 * The action filter options.
 */
export type BookkeepingManagerActionFilterOption = 'All' | 'ConfirmAccess'

/**
 * The action filter.
 */
export interface BookkeepingManagerActionFilter {
  count?: number
  type: BookkeepingManagerActionFilterOption
}
/**
 * Predefined instance of BookkeepingManagerActionFilter.
 */
const BookkeepingManagerActionFilter = {
  all: {
    count: 0,
    type: 'All',
  } as BookkeepingManagerActionFilter,
  confirmAccess: {
    count: 0,
    type: 'ConfirmAccess',
  } as BookkeepingManagerActionFilter,
}

/**
 * The filters used in the view model filter.
 */
export interface BookkeepingManagerFilter {
  workspaceIds: Id[]
  action: BookkeepingManagerActionFilter
}

/**
 * The sort fields.
 */
export interface BookkeepingManagerSort {
  field: BookkeepingPendingActionSortField
  direction: TableSortOrder
}

/**
 * The search filters.
 */
export interface BookkeepingManagerSearchFilter {
  workspaceIds: Id[]
  action?: BookkeepingManagerAction
  sort: BookkeepingManagerSort
  searchText: string
}

/**
 * Provides a predefined value and utility values to manage search filters.
 */
export const BookkeepingManagerSearchFilter = {
  none: {
    workspaceIds: [],
    sort: { field: 'DatePendingSince', direction: 'desc' },
    searchText: '',
  } as BookkeepingManagerSearchFilter,

  equals: (
    left: Partial<BookkeepingManagerSearchFilter>,
    right: Partial<BookkeepingManagerSearchFilter>,
  ) => deepEqual(left, right),
}

export class BookkeepingManagerSearchViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The filter of the last search, reused when realtime events refresh the list.
   */
  @observable.ref
  private accessor _lastSearchFilter = BookkeepingManagerSearchFilter.none

  /**
   * The searches$ observable combines filter changes, sort changes, and debounced
   * search text inputs into a stream of BookkeepingManagerSearchFilter objects,
   * and searches with each.
   */
  private readonly _searches$

  /**
   * The bookkeeping pending actions scroller.
   */
  readonly scroller = new InfiniteScrollProvider<
    BookkeepingPendingAction,
    BookkeepingManagerSearchFilter
  >({
    limit: 20,
    fetchItems: ({ paging, filter, signal }) => {
      const withRetry = this._createAlertRetryWrapper(
        'Loading bookkeeping requests failed',
        signal,
      )
      return withRetry(() =>
        this._bookkeepingManagerStore.listBookkeepingPendingActions(
          {
            ...paging,
            workspaceIds: filter?.workspaceIds,
            sort: filter?.sort,
            action: filter?.action,
            searchText: filter?.searchText,
          },
          signal,
        ),
      )
    },
  })

  /**
   * The bookkeeping pending actions filters.
   */
  readonly filters

  /**
   * The sorting field.
   */
  @observable
  accessor sorting: BookkeepingManagerSort = {
    field: 'DatePendingSince',
    direction: 'desc',
  }

  /**
   * The search text.
   */
  readonly searchText = new SearchTextViewModel()

  constructor(
    private readonly _bookkeepingManagerStore: BookkeepingManagerStore,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _listWorkspaceMemberships: ListWorkspaceMembershipsFn,
    private readonly _getWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _filterStorage?: FilterStorage,
  ) {
    this.filters = new FiltersViewModel<BookkeepingManagerFilter>({
      filterStorage: this._filterStorage,
      definitions: {
        workspaceIds: {
          title: 'Workspaces',
          implicit: true,
          factory: (key, title) =>
            new SelectFilter(
              key,
              title,
              FilterItemSource.static(this.getWorkspaces()),
              true,
            ),
        },
        action: {
          icon: 'pending',
          title: 'Quick Select',
          implicit: true,
          factory: (key, title) =>
            new SingleSelectFilter(
              title,
              key,
              BookkeepingManagerActionFilter.all,
              (signal) => this._fetchActionFilterOptions(signal),
            ),
        },
      },
    })

    this._searches$ = combineLatest([
      this.filters.appliedFilters$,
      toStream(() => this.sorting, true),
      this.searchText.debouncedValue$,
    ]).pipe(
      map(
        ([
          appliedFilters,
          sort,
          searchText,
        ]): BookkeepingManagerSearchFilter => ({
          workspaceIds: appliedFilters.workspaceIds ?? [],
          action: this._toBookkeepingManagerAction(appliedFilters.action?.type),
          sort,
          searchText,
        }),
      ),
      // Ignore emissions with the same search filter.
      distinctUntilChanged<BookkeepingManagerSearchFilter>(
        BookkeepingManagerSearchFilter.equals,
      ),
      // Realtime refreshes reuse the filter, also when its search failed.
      tapAction((searchFilter) => (this._lastSearchFilter = searchFilter)),
      switchMap((query) =>
        withAbortSignal((signal) =>
          this.scroller.fetch({ filter: query, signal }),
        ).pipe(
          // The retry alert has reported the failure.
          catchError(() => EMPTY),
        ),
      ),
      // Make sure to only subscribe once.
      shareLatest(),
    )

    this._lifecycle.manage(
      this._bookkeepingManagerStore.events$.pipe(
        tap(() => {
          // The retry alert has reported a failure.
          void this.scroller
            .fetch({
              filter: this._lastSearchFilter,
              signal: this._lifecycle.signal,
              staleWhileRevalidate: true,
            })
            .catch(() => undefined)
          this.reloadActionFilter()
        }),
      ),
    )
    this._lifecycle.manage(this._searches$)
    // Search text is cleared and the action filter reloaded only when the
    // workspaces change.
    this._lifecycle.manage(
      this.filters.appliedFilters$.pipe(
        map((a) => a.workspaceIds),
        distinctUntilChanged(),
        tapAction(() => {
          this.searchText.clear()
          this.reloadActionFilter()
        }),
      ),
    )
  }

  /**
   * The workspace filter.
   */
  @computed
  get workspaceFilter() {
    return this.filters.getFilter('workspaceIds') as SelectFilter<string, Id>
  }

  /**
   * The action filter.
   */
  @computed
  get actionFilter() {
    return this.filters.getFilter('action') as SingleSelectFilter<
      string,
      BookkeepingManagerActionFilter
    >
  }

  /**
   * The bookkeeping pending actions filtered.
   */
  @computed
  get filteredItems() {
    return this.scroller.items
  }

  /**
   * The total of bookkeeping pending actions.
   */
  @computed
  get total() {
    return (
      this.actionFilter.options.find((x) => x.value?.type === 'All')?.value
        .count ?? 0
    )
  }

  /**
   * Sets the sort method.
   */
  @action.bound
  setSortMethod(sortMethod: BookkeepingManagerSort) {
    this.sorting = sortMethod
  }

  /**
   * Sets the sorting direction.
   */
  @action.bound
  setSortDirection(direction: TableSortOrder) {
    // If we have no direction remove any sorting.
    if (direction === 'none') {
      this.setSortMethod({
        field: 'DatePendingSince',
        direction: 'none',
      })
      return
    }

    this.setSortMethod({
      direction,
      field: this.sorting.field,
    })
  }

  /**
   * Reloads the totals of the action filter.
   */
  reloadActionFilter() {
    // The cards keep their previous totals when the reload fails.
    void this.actionFilter
      .fetchOptions(this._lifecycle.signal)
      .catch(() => undefined)
  }

  /**
   * Removes bookkeeping pending action from the items.
   *
   * @param id
   */
  @action.bound
  removeBookkeepingPendingAction(id: Id) {
    this.scroller.removeWhere((x) => x.id === id)
  }

  /**
   * Sets the sort filed.
   */
  @action.bound
  setSortField(field: BookkeepingPendingActionSortField) {
    this.setSortMethod({
      field,
      direction: this.sorting.direction,
    })
  }

  /**
   * Gets workspaces.
   */
  getWorkspaces(): FilterItem<Id>[] {
    return this._getWorkspaceMemberships()
      .filter((mb) => mb.member.permissions.manageBookkeeping)
      .map((membership) => {
        return {
          title: membership.workspace.name,
          value: membership.workspace.id,
        }
      })
  }

  /**
   * Fetches the workspaces available to filter by.
   */
  async fetchWorkspaces(signal?: AbortSignal) {
    await this._listWorkspaceMemberships({}, signal)
    await this.workspaceFilter.fetchOptions(signal)
  }

  /**
   * Activates the vm. Filters are activated before the searches subscribe,
   * otherwise the first search runs without the stored filters.
   */
  activate() {
    this.filters.activate()
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
    this.filters.deactivate()
  }

  /**
   * Maps manager action filter options to manager action.
   */
  private _toBookkeepingManagerAction(
    action?: BookkeepingManagerActionFilterOption,
  ): BookkeepingManagerAction | undefined {
    if (!action) {
      return undefined
    }

    switch (action) {
      case 'ConfirmAccess':
        return 'ConfirmAccess'
      case 'All':
        return undefined
    }
  }

  /**
   * Gets the action filters.
   */
  private async _fetchActionFilterOptions(
    signal?: AbortSignal,
  ): Promise<FilterItem<BookkeepingManagerActionFilter>[]> {
    const withRetry = this._createAlertRetryWrapper(
      'Loading bookkeeping request counts failed',
      signal,
    )
    const projection = await withRetry(() =>
      this._bookkeepingManagerStore.fetchBookkeepingPendingActionAggregations(
        { workspaceIds: this.workspaceFilter.value },
        signal,
      ),
    )

    return [
      {
        title: 'Total requests',
        value: {
          type: 'All',
          count: projection.total,
        },
      },
      {
        title: 'Confirm access',
        value: {
          type: 'ConfirmAccess',
          count: projection.confirmAccessTotal,
        },
      },
    ]
  }
}
